import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { Route, Switch, useHistory, useParams } from 'react-router-dom'
import Button from '@react/react-spectrum/Button'
import Heading from '@react/react-spectrum/Heading'
import { Tab, TabView } from '@react/react-spectrum/TabView'
import { error as errorToast, info } from '@react/react-spectrum/Toast'
import Wait from '@react/react-spectrum/Wait'
import A from 'services/analytics'
import ChevronLeft from '@react/react-spectrum/Icon/ChevronLeft'
import Gears from '@react/react-spectrum/Icon/Gears'
import Share from '@react/react-spectrum/Icon/Share'
import UsersShare from '@react/react-spectrum/Icon/UsersShare'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Delete from '@react/react-spectrum/Icon/Delete'

import { archiveProject, deleteLaunchProperty, deleteDataStream } from 'services/project'
import BrandingForm from 'fragments/BrandingForm'
import DataElementsForm from 'fragments/DataElementsForm'
import InsightsTab from 'fragments/InsightsTab'
import IntegrationsForm from 'fragments/IntegrationsForm'
import ModuleManager from 'fragments/ModuleManager'
import NavigationForm from 'fragments/NavigationForm'
import ProductsForm from 'fragments/ProductsForm'
import ProfileViewerEditor from 'fragments/ProfileViewer'
import RulesForm from 'fragments/RulesForm'
import ScreenStructure from 'fragments/ScreenStructure'
import SettingsForm from 'fragments/SettingsForm'
import SharingForm from 'fragments/SharingForm'
import TriggersForm from 'fragments/TriggersForm'
import UserContextForm from 'fragments/UserContextForm'

import { DeleteProjectDialog } from 'components/DeleteProject'

import { PATHS } from 'config/constants'
import { ProjectContextProvider, useFeatures, useProject } from 'hooks'
import SaveIndicator from 'components/SaveIndicator'

import DecisioningSurfaces from '../DecisioningSurfaces/DecisioningSurfaces'
import ScreenBuilder from './ScreenBuilder'
import { AccessForm, RunProjectButton } from './partials'

import './Project.scss'

const TABS = [
  {
    name: 'screens',
    label: 'Screens',
    path: PATHS.SCREENS_TAB,
    content: ScreenStructure,
  },
  {
    name: 'navigation',
    label: 'Navigation',
    path: PATHS.NAVIGATION_TAB,
    content: NavigationForm,
  },
  {
    name: 'branding',
    label: 'Branding',
    path: PATHS.BRANDING_TAB,
    content: BrandingForm,
  },
  {
    name: 'integrations',
    label: 'Integrations',
    path: PATHS.INTEGRATIONS_TAB,
    content: IntegrationsForm,
  },
  {
    name: 'triggers',
    label: 'Triggers',
    path: PATHS.TRIGGERS_TAB,
    content: TriggersForm,
  },
  {
    name: 'profile-viewer',
    label: 'Profile Viewer',
    path: PATHS.PROFILE_VIEWER,
    content: ProfileViewerEditor,
  },
  {
    name: 'products',
    label: 'Products',
    path: PATHS.PRODUCTS,
    content: ProductsForm,
  },
  {
    name: 'modules',
    label: 'Modules',
    path: PATHS.MODULES,
    content: ModuleManager,
  },
  {
    name: 'userContext',
    label: 'User',
    path: PATHS.USER_CONTEXT,
    content: UserContextForm,
  },
  {
    icon: <UsersShare />,
    name: 'access',
    label: 'Access',
    path: PATHS.ACCESS,
    content: AccessForm,
  },
  {
    icon: <Gears />,
    name: 'settings',
    label: 'Settings',
    path: PATHS.SETTINGS,
    content: SettingsForm,
  },
  {
    icon: <Share />,
    name: 'share',
    label: 'Share',
    path: PATHS.SHARE,
    content: SharingForm,
  },
  {
    name: 'insights',
    label: 'Insights',
    path: PATHS.INSIGHTS,
    content: InsightsTab,
  },
]

const DATA_ELEMENTS_TAB = {
  name: 'data-elements',
  label: 'Data Elements',
  path: PATHS.DATA_ELEMENTS_TAB,
  content: DataElementsForm,
}

const RULES_TAB = {
  name: 'rules',
  label: 'Rules',
  path: PATHS.RULES_TAB,
  content: RulesForm,
}

const DECISIONING_SURFACES_TAB = {
  name: 'decisioning-surfaces',
  label: 'Decisioning Surfaces',
  path: PATHS.DECISIONING_SURFACES,
  content: DecisioningSurfaces,
}

function Project() {
  const history = useHistory()
  const { projectId, tab: currentTab } = useParams()
  const { isDataElementsAndRulesTabsEnabled, isDecisioningSurfacesTabEnabled } = useFeatures()
  const { loading, error, project, config, canEdit, canDelete, type } = useProject()

  useEffect(() => {
    if (projectId === project?.id && !canEdit && !project.isFeatured) {
      info('You are viewing the project in read-only mode. Please contact project owner for edit access.')
    }
  }, [canEdit, project, projectId])
  const propertyID = project?.integrations?.launch?.propertyID

  const projectDeleteHandler = async (deleteTags) => {
    if (!canDelete) return

    if (deleteTags && propertyID) {
      await deleteDataStream(project?.integrations?.launch?.id)
      deleteLaunchProperty(project?.integrations?.launch?.id)
    }
    A.setProject(project)
    A.triggerDeleteProject()
    A.clearProject()
    archiveProject(project?.meta?.id)

    history.push(PATHS.PROJECT_LIST)
  }

  const tabs = useMemo(() => {
    if (!type) return
    let allTabs = TABS
    const tabOrder = (config?.tabs || []).concat('settings', 'access')

    if (type === 'external-website') {
      if (isDecisioningSurfacesTabEnabled) allTabs = allTabs.concat([DECISIONING_SURFACES_TAB])
      if (isDataElementsAndRulesTabsEnabled) allTabs = allTabs.concat([DATA_ELEMENTS_TAB, RULES_TAB])
    }

    return tabOrder.map((tabName) => allTabs.find((t) => t.name === tabName))?.filter(Boolean) || []
  }, [config, isDataElementsAndRulesTabsEnabled, isDecisioningSurfacesTabEnabled, type])

  const onBack = useCallback(() => {
    history.push(PATHS.PROJECT_LIST)
  }, [history])

  const navigateToTab = useCallback(
    (tabName) => history.push(`${PATHS.PROJECT_DETAILS}${projectId}${tabName}`),
    [history, projectId]
  )

  const onTabSelect = useCallback((idx) => navigateToTab(tabs[idx].path), [navigateToTab, tabs])

  if (loading) {
    return <ProjectLoadingState />
  }

  if (error) {
    const { code, message } = error
    errorToast(message)
    if (['NOT_ALLOWED', 'PROJECT_NOT_FOUND', 'PROJECT_ARCHIVED'].includes(code)) {
      history.push(PATHS.HOME)
      return <></>
    }
  }

  return (
    <div className='Project'>
      {project && (
        <div className='Project__header'>
          <span className='Project__title'>
            <Button
              icon={<ChevronLeft size='M' />}
              variant='tool'
              onClick={onBack}
              className='Project__backButton'
              aria-label='Back to project list'
            />
            <Heading size={2}>{project?.meta?.title}</Heading>
            <SaveIndicator updatedAt={project.updatedAt} />
          </span>

          <div>
            {canDelete && (
              <ModalTrigger>
                <Button variant='primary' icon={<Delete size={'XS'} />} label='Delete' />
                <DeleteProjectDialog
                  projectId={project?.meta?.id}
                  title={project.meta.title}
                  propertyID={propertyID}
                  onSubmit={projectDeleteHandler}
                />
              </ModalTrigger>
            )}
            <RunProjectButton project={project} />
          </div>
        </div>
      )}
      <MemoizedTabs tabs={tabs} currentTab={currentTab} onSelect={onTabSelect} />
    </div>
  )
}

function ProjectContainer() {
  return (
    <ProjectContextProvider>
      <Switch>
        <Route
          exact
          path={`${PATHS.PROJECT_DETAILS}:projectId${PATHS.SCREENS_TAB}/:screenName`}
          component={ScreenBuilder}
        />
        <Route exact path={`${PATHS.PROJECT_DETAILS}:projectId/:tab/:itemId`} component={Project} />
        <Route path={`${PATHS.PROJECT_DETAILS}:projectId/:tab?`} component={Project} />
      </Switch>
    </ProjectContextProvider>
  )
}

function Tabs({ tabs, currentTab, onSelect }) {
  const currentIndex = useMemo(() => tabs.findIndex((t) => t.name === currentTab), [tabs, currentTab])
  if (tabs?.length > 0) {
    return (
      <TabView onSelect={onSelect} selectedIndex={currentIndex < 0 ? 0 : currentIndex} className='Project__tabs'>
        {tabs.map(({ label, icon, disabled, content: Content }) => (
          <Tab key={label} label={label} icon={icon} disabled={disabled}>
            <Content />
          </Tab>
        ))}
      </TabView>
    )
  } else return <></>
}

const MemoizedTabs = memo(Tabs)

export function ProjectLoadingState() {
  return (
    <div className='ProjectLoadingState'>
      <div className='wait-wrapper'>
        <Wait centered size='L' />
        <em>Loading your project...</em>
      </div>
    </div>
  )
}

export default ProjectContainer
